import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { api } from '../../../../../api';
import { Enrolment } from '../../../../../api/types/enrolment';
import MenuMeatballsIcon from '../../../../../assets/images/icon-outline-menu-meatball-white.svg';
import DropdownMenu from '../../../../../components/DropdownMenu/DropdownMenu';
import { SkeletonLoader } from '../../../../../components/SkeletonLoader';
import { getPausedEnrolments } from '../../../../../selectors/hubSelector';
import { ENROLMENT_STATUS } from '../../../../../types/hubEnums';
import { PausedEnrolment } from '../../../../../types/hubInterfaces';
import { formatError } from '../../../../../util/errors';
import { formatSession } from '../../../../../util/helpers';
import { PATH_HUB_CR_CANCEL_ENROLMENT } from '../../../../../util/pagePath';
import pluralize from '../../../../../util/pluralize';
import useSubscriptionContext, { CANCELLATION_TYPE } from '../../useSubscriptionContext';

export const EnrolmentDetails = ({
  enrolment,
  setSelectedEnrolment,
  showCancelEnrolment = true,
}: {
  enrolment: Enrolment;
  setSelectedEnrolment: (enrolment: Enrolment) => void;
  showCancelEnrolment?: boolean;
}) => {
  const history = useHistory();
  const {
    clueyPlus: {
      details: { setShow: setDetailsShow },
      showCancellationDialog,
    },
    sessions: {
      upcoming: { sessions: upcomingSessions, isLoading: isLoadingUpcomingSessions },
      completed: { sessions: completedSessions, isLoading: isLoadingCompletedSessions },
    },
  } = useSubscriptionContext();

  const [formattedPaymentDate, setFormattedPaymentDate] = useState<string>('');

  const { data: accountDetails } = api.account.details.useQuery();
  const {
    data: enrolmentDetail,
    isLoading: isLoadingEnrolmentDetail,
    error: enrolmentDetailError,
  } = api.enrolment.detail.useQuery({ enrolmentId: enrolment.enrolmentId }, { enabled: !!enrolment.enrolmentId });

  const enrolmentIsNotCancelled = ![ENROLMENT_STATUS.CANCEL_REQUESTED].includes(enrolment.enrolmentStatus);
  const enrolmentIsPaused = enrolment.enrolmentStatus === ENROLMENT_STATUS.PAUSED;
  const upcomingSessionsForEnrolment = upcomingSessions?.filter(
    (session) => session.uniqueEnrolmentId === enrolment.enrolmentId
  );
  const lastUpcomingSession = [...(upcomingSessionsForEnrolment ?? [])]
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
    .at(-1);
  const completedSessionsForEnrolment = completedSessions?.filter(
    (session) => session.uniqueEnrolmentId === enrolment.enrolmentId
  );
  const upcomingSessionsCount = upcomingSessionsForEnrolment?.length ?? 0;
  const pausedEnrolments: PausedEnrolment[] = useSelector(getPausedEnrolments);
  const pausedEnrolment = pausedEnrolments.find(
    (pausedEnrolment) => pausedEnrolment.enrolmentId === enrolment.enrolmentId
  );

  const { date: dayOfSession, time: timeOfSession } = formatSession({
    session:
      upcomingSessionsForEnrolment && upcomingSessionsForEnrolment.length > 0
        ? upcomingSessionsForEnrolment?.[0]
        : completedSessionsForEnrolment?.[0],
    timezone: accountDetails?.timezone,
    sessionDateFormat: 'dddd',
    useSemiColon: false,
  });

  useEffect(() => {
    if (enrolmentDetail) {
      setFormattedPaymentDate(
        moment(enrolmentDetail.nextPaymentDate).tz(accountDetails?.timezone).format('D MMM YYYY')
      );
    }
  }, [enrolmentDetail, isLoadingEnrolmentDetail, accountDetails?.timezone]);

  const isLoading = isLoadingUpcomingSessions || isLoadingCompletedSessions;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-y-4">
        <SkeletonLoader loading={isLoading} className="h-28 w-full" />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-y-0 border-b border-white/20 pb-4 last:border-b-0 last:pb-2"
      key={`${enrolment.enrolmentId}-${enrolment.subject}-details`}
    >
      {enrolmentDetailError && (
        <div className="text-sm font-semibold text-red-4">{formatError(enrolmentDetailError)}</div>
      )}
      <div className="flex flex-row justify-between">
        <div className="flex flex-row items-center gap-x-2">
          <h2 className="merriweather text-lg font-bold text-white">{enrolment.subject}</h2>
          {!enrolmentIsNotCancelled && (
            <span className="rounded-full bg-[#E4DED7] px-2 py-1 text-xs font-semibold text-grey-6">Cancelled</span>
          )}
          {enrolmentIsPaused && (
            <span className="rounded-full bg-[#FFB98A] px-2 py-1 text-xs font-semibold text-grey-6">Paused</span>
          )}
        </div>
        {enrolmentIsNotCancelled && (
          <DropdownMenu
            items={[
              {
                label: 'Manage enrolment',
                onClick: () => history.push(`${PATH_HUB_CR_CANCEL_ENROLMENT}?token=${enrolment.enrolmentId}`),
              },
              ...(showCancelEnrolment
                ? [
                    {
                      label: `Cancel ${enrolment.subject} tutoring`,
                      onClick: () => {
                        setSelectedEnrolment(enrolment);
                        showCancellationDialog(CANCELLATION_TYPE.CANCEL_TUTORIAL);
                        setDetailsShow(false);
                      },
                    },
                  ]
                : []),
            ]}
            icon={<img src={MenuMeatballsIcon} alt="menu icon" />}
            showIcon={false}
            buttonClassName="border-none !px-1 !h-5"
            itemClassName="!py-3 !px-5 rounded-lg"
            dropdownClassName="!min-w-[300px]"
          />
        )}
      </div>
      {!enrolmentIsNotCancelled && upcomingSessionsCount > 0 && (
        <div className="flex flex-row items-center gap-x-2 py-1">
          <span className="text-sm font-semibold text-blood-orange-3">
            {upcomingSessionsCount} {pluralize('session', upcomingSessionsCount)} remaining
          </span>
        </div>
      )}
      <div className="flex flex-row items-center gap-x-2">
        <span className="text-sm font-medium text-green-3">
          {timeOfSession?.toString().replaceAll(' ', '')} {dayOfSession}s
        </span>
      </div>
      {!enrolmentIsNotCancelled && upcomingSessionsCount > 0 && (
        <div className="flex flex-row items-center gap-x-2">
          <span className="text-sm font-semibold text-white">
            Last session: {moment(lastUpcomingSession.startTime).format('D MMM YYYY')}
          </span>
        </div>
      )}
      {enrolmentIsNotCancelled && !enrolmentIsPaused && (
        <>
          {!isLoadingEnrolmentDetail && formattedPaymentDate ? (
            <div className="flex flex-row items-center gap-x-2">
              <span className="text-sm font-semibold text-blood-orange-3">Next payment:</span>{' '}
              <span className="text-sm font-semibold text-white">{formattedPaymentDate}</span>
            </div>
          ) : (
            <SkeletonLoader loading={isLoadingEnrolmentDetail} className="h-5 w-3/4" />
          )}
        </>
      )}
      {enrolmentIsNotCancelled && enrolmentIsPaused && pausedEnrolment && (
        <>
          <div className="flex flex-row items-center gap-x-2">
            <span className="text-sm font-semibold text-blood-orange-3">Resume on:</span>{' '}
            <span className="text-sm font-semibold text-white">
              {moment(pausedEnrolment.resumeSessionDate).tz(accountDetails?.timezone).format('D MMM YYYY')}
            </span>
          </div>
        </>
      )}
    </div>
  );
};
